import clsx from 'clsx';
import React from 'react';

export type TypographyVariant =
  | 'body'
  | 'body-dark'
  | 'body-label'
  | 'title-default'
  | 'body-large'
  | 'body-large-semibold'
  | 'footnote'
  | 'footnote-2'
  | 'footnote-semibold'
  | 'form-label'
  | 'title-small'
  | 'body-semibold'
  | 'body-bold'
  | 'footer'
  | 'success-note'
  | 'error'
  | 'sub-title'
  | 'banner'
  | 'title-large';

export type TypographyProps<T extends React.ElementType> = {
  variant: TypographyVariant;
  as?: T;
  className?: string;
} & React.ComponentPropsWithoutRef<T>;

export function Typography<T extends React.ElementType = 'span'>({
  as,
  variant,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'span';
  return (
    <Component
      className={clsx(
        className,
        'leading-snug',
        variant === 'body' && 'text-base text-gray-soft tracking-[0.427px]',
        variant === 'body-dark' &&
          'text-base text-black-dark tracking-[0.427px]',
        variant === 'body-label' &&
          'text-black-dark tracking-[0.427px] font-semibold text-base',
        variant === 'title-default' &&
          'text-2xl text-gray-soft font-bold tracking-[0.828px] font-futura-pt',
        variant === 'sub-title' &&
          'text-xl font-demibold tracking-[0.668px] font-futura-pt',
        variant === 'body-large' && 'text-lg text-gray-soft tracking-[0.534px]',
        variant === 'body-large-semibold' &&
          'text-lg text-black-dark font-semibold tracking-[0.534px]',

        variant === 'footnote' && 'text-xs text-gray-darkest',
        variant === 'footnote-2' && 'text-xs text-gray-soft',
        variant === 'form-label' && 'text-sm text-gray-darkest',
        variant === 'title-small' &&
          'text-lg font-demibold  tracking-[0.534px] font-futura-pt',
        variant === 'body-semibold' && 'tracking-[0.427px] font-semibold',
        variant === 'body-bold' && 'tracking-[0.427px] font-bold',
        variant === 'footer' &&
          'tracking-wide font-semibold text-[10px] text-gray-soft',
        variant === 'footnote-semibold' &&
          'text-xs font-semibold text-gray-darkest leading-5',
        variant === 'success-note' && 'text-green-dark',
        variant === 'error' && 'text-red-dark',
        variant === 'banner' && 'text-white font-futura-pt text-lg',
        variant === 'title-large' &&
          'font-futura-pt text-3xl tracking-[1.041px] font-demibold',
      )}
      {...props}
    >
      {children}
    </Component>
  );
}
